import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Botao, Selo } from '../componentes/base';
import { Modal } from '../componentes/sobreposicao';
import { get, patch, post } from '../nucleo/api';
import type { Idioma } from '../nucleo/formato';
import { useOpcoes } from '../nucleo/notificacoes/dados';
import { useSessao } from '../nucleo/sessao';
import { NOVIDADES, novidadesPendentes, type Novidade } from './index';

export const CHAVE_SESSAO_NOVIDADES = 'malgoos.novidades.sessao';

function jaMostrouNestaSessao() {
  try {
    return sessionStorage.getItem(CHAVE_SESSAO_NOVIDADES) === '1';
  } catch {
    return false;
  }
}
function marcarSessao() {
  try {
    sessionStorage.setItem(CHAVE_SESSAO_NOVIDADES, '1');
  } catch {
    /* sem storage: no máximo repete na próxima carga */
  }
}

/** Mídia do passo ou da demonstração; com movimento reduzido, só o pôster estático. */
function Midia({ n, passo, alt }: { n: Novidade; passo: number; alt: string }) {
  const reduzido = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  if (n.demo && passo === 0) {
    return reduzido || !n.demo.arquivo.endsWith('.mp4') ? <img src={reduzido ? n.demo.poster : n.demo.arquivo} alt={alt} className="w-full rounded-lg border border-borda" /> : <video src={n.demo.arquivo} poster={n.demo.poster} muted loop autoPlay playsInline aria-label={alt} className="w-full rounded-lg border border-borda" />;
  }
  const img = n.imagens?.[passo];
  return img ? <img src={img} alt={alt} loading="lazy" className="w-full rounded-lg border border-borda" /> : null;
}

/** Quantas novidades vistas vão por pedido (a rota aceita até 50; o servidor guarda as 500 mais recentes). */
export const LOTE_NOVIDADES_VISTAS = 50;

/**
 * Review r17 robustez (B5): marca as novidades como vistas em lotes de 50, um pedido depois do outro (o primeiro leva as
 * outras opções). Antes ia tudo num pedido só: com o catálogo passando de 50, a pessoa nova levava 422 e as novidades
 * nunca ficavam vistas (o mesmo que a W24 corrigiu no aviso do sino).
 */
export async function marcarNovidadesVistas(ids: readonly string[], opcoes: { novidades_popup?: boolean } = {}) {
  const lotes = Array.from({ length: Math.max(1, Math.ceil(ids.length / LOTE_NOVIDADES_VISTAS)) }, (_, i) => ids.slice(i * LOTE_NOVIDADES_VISTAS, (i + 1) * LOTE_NOVIDADES_VISTAS));
  for (const [i, lote] of lotes.entries()) await patch('/notificacoes/opcoes', { novidades_vistas: lote, ...(i === 0 ? opcoes : {}) });
}

/**
 * Fase 2 (W3, análise A 1.5): pop-up de novidades. No máximo um por sessão, depois do tour (nunca com ele aberto),
 * em carrossel quando há mais de uma. "Entendi", "Ver treinamento", "Ir para a tela" e "Não mostrar novidades em
 * pop-up". Com o pop-up desligado, a novidade vira aviso no sino (uma vez).
 */
export function PopupNovidades() {
  const s = useSessao();
  const qc = useQueryClient();
  const opcoes = useOpcoes();
  const [lista, setLista] = useState<Novidade[]>([]);
  const decidiu = useRef(false);

  const concluir = useMutation({
    mutationFn: (p: { ids: string[]; naoMostrar: boolean }) => marcarNovidadesVistas(p.ids, p.naoMostrar ? { novidades_popup: false } : {}),
    onSettled: () => qc.invalidateQueries({ queryKey: ['notificacoes'] }),
  });

  useEffect(() => {
    if (decidiu.current || !opcoes.data || !s.usuario.onboarding_concluido || jaMostrouNestaSessao()) return;
    const pendentes = novidadesPendentes(NOVIDADES, { vistas: opcoes.data.novidades_vistas, usuarioCriadoEm: opcoes.data.usuario_criado_em, instalado: s.instalado, pode: s.pode });
    decidiu.current = true;
    if (!pendentes.length) return;
    if (opcoes.data.novidades_popup) {
      marcarSessao();
      setLista(pendentes);
    } else {
      // Sem pop-up: vai ao sino uma vez e fica como vista.
      // Fase 2 (W24): a rota aceita até 20 ids por pedido; com o catálogo passando de 20, vai em lotes (antes dava 422).
      const ids = pendentes.map((n) => n.id);
      const lotes = Array.from({ length: Math.ceil(ids.length / 20) }, (_, i) => ids.slice(i * 20, i * 20 + 20));
      void lotes
        .reduce<Promise<unknown>>((anterior, lote) => anterior.then(() => post('/notificacoes/novidades/avisar', { ids: lote })), Promise.resolve())
        .then(() => marcarNovidadesVistas(ids));
    }
  }, [opcoes.data, s.usuario.onboarding_concluido, s.instalado, s.pode]);

  if (!lista.length) return null;
  return (
    <ModalNovidades
      lista={lista}
      comOpcaoPopup
      aoFechar={({ naoMostrar }) => {
        concluir.mutate({ ids: lista.map((x) => x.id), naoMostrar });
        setLista([]);
      }}
    />
  );
}

/**
 * Diálogo das novidades em carrossel (o do pop-up). Fase 2 (W21, K7): também aberto pelo card "Novidades" do Início, na
 * novidade escolhida (`inicio`). `aoFechar` recebe as novidades que a pessoa chegou a ver e a escolha de "não mostrar em
 * pop-up" (só no pop-up). "Ver treinamento" abre a aula da novidade; "Ir para a tela", a tela do recurso.
 */
export function ModalNovidades({ lista, inicio = 0, comOpcaoPopup = false, aoFechar }: { lista: Novidade[]; inicio?: number; comOpcaoPopup?: boolean; aoFechar: (r: { vistas: string[]; naoMostrar: boolean }) => void }) {
  const { t, i18n } = useTranslation();
  const s = useSessao();
  const navegar = useNavigate();
  const [i, setI] = useState(() => Math.min(Math.max(inicio, 0), Math.max(lista.length - 1, 0)));
  const [vistas, setVistas] = useState<Set<number>>(() => new Set([Math.min(Math.max(inicio, 0), Math.max(lista.length - 1, 0))]));
  const [naoMostrar, setNaoMostrar] = useState(false);
  const idioma = (['pt', 'en', 'es'].includes(i18n.language) ? i18n.language : 'pt') as Idioma;
  const n = lista[i]!;
  const tx = n.textos[idioma];
  const ir = (k: number) => {
    setI(k);
    setVistas((v) => new Set(v).add(k));
  };
  const fechar = (destino?: string) => {
    aoFechar({ vistas: [...vistas].map((k) => lista[k]!.id), naoMostrar });
    if (destino) navegar(destino);
  };
  // Fase 2 (W17, K1): abre a aula da novidade no curso publicado; sem a aula, o curso; sem o curso publicado, a busca.
  const verTreinamento = async () => {
    const r = await get<{ id: string; publicado: boolean; aula_id: string | null }>(`/treinamento/cursos/sistema/${n.treinamento!.curso}?aula=${encodeURIComponent(n.id)}`).catch(() => null);
    fechar(r?.publicado ? `/treinamento/treinamento/curso/${r.id}${r.aula_id ? `?aula=${r.aula_id}` : ''}` : `/treinamento/treinamento/cursos?busca=${encodeURIComponent(tx.titulo)}`);
  };
  const temTreinamento = !!n.treinamento && s.instalado('treinamento') && s.pode('treinamento.ver');
  return (
    <Modal
      aberto
      aoFechar={() => fechar()}
      largura="lg"
      titulo={
        <span className="flex flex-wrap items-center gap-2">
          {t('novidades.titulo')}
          <Selo cor="acento">{t(`novidades.tipos.${n.tipo}`)}</Selo>
          {lista.length > 1 && <span className="numero text-xs font-normal text-tinta-3">{t('novidades.contador', { atual: i + 1, total: lista.length })}</span>}
        </span>
      }
      rodape={
        <>
          {comOpcaoPopup && (
            <label className="mr-auto flex items-center gap-2 text-sm text-tinta-2">
              <input type="checkbox" checked={naoMostrar} onChange={(e) => setNaoMostrar(e.target.checked)} className="size-4 accent-acento-forte" />
              {t('novidades.naoMostrar')}
            </label>
          )}
          {temTreinamento && (
            <Botao variante="fantasma" onClick={() => void verTreinamento()}>
              {t('novidades.verTreinamento')}
            </Botao>
          )}
          {n.rota && (
            <Botao variante="secundario" onClick={() => fechar(n.rota)}>
              {t('novidades.irParaTela')}
            </Botao>
          )}
          <Botao variante="primario" onClick={() => fechar()}>
            {t('novidades.entendi')}
          </Botao>
        </>
      }
    >
      <article data-novidade={n.id} className="flex flex-col gap-3">
        <h3 className="text-lg font-semibold text-tinta-1">{tx.titulo}</h3>
        <p className="text-sm text-tinta-2">{tx.resumo}</p>
        <Midia n={n} passo={0} alt={tx.titulo} />
        <h4 className="kicker">{t('novidades.passos')}</h4>
        <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm text-tinta-1">
          {tx.passos.map((p, k) => (
            <li key={k}>
              {p}
              {k > 0 && <div className="mt-2"><Midia n={n} passo={k} alt={p} /></div>}
            </li>
          ))}
        </ol>
        {lista.length > 1 && (
          <div className="flex justify-between">
            <Botao variante="fantasma" tamanho="sm" disabled={i === 0} onClick={() => ir(i - 1)} icone={<ChevronLeft className="size-4" aria-hidden />}>
              {t('novidades.anterior')}
            </Botao>
            <Botao variante="fantasma" tamanho="sm" disabled={i === lista.length - 1} onClick={() => ir(i + 1)} icone={<ChevronRight className="size-4" aria-hidden />}>
              {t('novidades.proxima')}
            </Botao>
          </div>
        )}
      </article>
    </Modal>
  );
}
